import { useRef, useState } from 'react'

import {
    navigateTo,
    patternRoute,
    prefixRoute,
    simpleRoute,
} from '../../common/frontend/route-utils.js'
import { t } from '../../common/i18n.js'
import {
    PlaytimeCampaignWithQuestions,
    PlaytimeLeaderboardData,
    PlaytimeProfileData,
} from '../../common/types/playtime.js'
import { previewCampaignFromForm } from '../../common/utils/q12-preview.js'
import { PageHeader } from '../components/page-header/page-header.js'
import { PageShell } from '../components/page-shell/page-shell.js'
import { Spinner } from '../components/spinner/spinner.js'
import { getBaseProps } from '../props/base.js'
import { DataState, MenuItem, View } from '../types.js'
import { handleError } from '../utils/error-utils.js'
import {
    loadPlaytimeLeaderboardIfNeeded,
    loadPlaytimeProfileIfNeeded,
} from '../utils/load-utils.js'
import { ViewBaseProps } from '../views/base/base.js'
import { legacyBadgeRequestTarget } from '../views/playtime/badge-nav.js'
import { PlaytimeProfileView } from '../views/playtime/profile/profile.js'
import { SurveyView } from '../views/playtime/survey/survey.js'

class PlaytimeModel {
    public profileRoute = simpleRoute<View>('/badges', {
        onEnter: (view) => {
            loadPlaytimeProfileIfNeeded(view)
            loadPlaytimeLeaderboardIfNeeded(view)
        },
        render: (view) => {
            const profile = loadPlaytimeProfileIfNeeded(view)
            const leaderboard = loadPlaytimeLeaderboardIfNeeded(view)
            return (
                <PlaytimeProfileView
                    base={getBaseProps(view)}
                    profile={profile}
                    leaderboard={leaderboard}
                    totalBadgesCount={profile.data?.badges.length ?? 0}
                    view={view}
                />
            )
        },
    })

    public surveyRoute = patternRoute<View, '/survey/:campaignId'>('/survey/:campaignId', {
        render: (view, params) => {
            return (
                <SurveyViewWrapper
                    base={getBaseProps(view)}
                    campaignId={Number(params.campaignId)}
                    view={view}
                />
            )
        },
    })

    // Admin previews (TASK-313): a form on its own, or a campaign as the
    // respondent will see it. Nothing is submitted from either.
    public formPreviewRoute = patternRoute<View, '/survey/preview/form/:formId'>(
        '/survey/preview/form/:formId',
        {
            render: (view, params) => (
                <SurveyPreviewWrapper
                    key={`form-${params.formId}`}
                    base={getBaseProps(view)}
                    view={view}
                    load={async () => {
                        const res = await view.api.playtime.getForm(Number(params.formId))
                        return previewCampaignFromForm(res.data, formatIsoDate(new Date()))
                    }}
                />
            ),
        },
    )

    public campaignPreviewRoute = patternRoute<View, '/survey/preview/campaign/:campaignId'>(
        '/survey/preview/campaign/:campaignId',
        {
            render: (view, params) => (
                <SurveyPreviewWrapper
                    key={`campaign-${params.campaignId}`}
                    base={getBaseProps(view)}
                    view={view}
                    load={async () =>
                        (await view.api.playtime.getCampaign(Number(params.campaignId))).data
                    }
                />
            ),
        },
    )

    // Preserve already-shared survey links: /badges/survey/... → /survey/...
    public legacySurveyRoute = prefixRoute<View>('/badges/survey', {
        onEnter: () => {
            navigateTo(window.location.pathname.replace(/^\/badges\/survey/, '/survey'))
        },
        render: () => <Spinner />,
    })

    // Old links (e-mails, Slack) to the separate claim page: the claim form now lives in
    // the badge modal, so open that badge on /badges instead.
    public badgeRequestRoute = patternRoute<View, '/badges/badge-request/:badgeId'>(
        '/badges/badge-request/:badgeId',
        {
            onEnter: (_view, params) => {
                navigateTo(legacyBadgeRequestTarget(params.badgeId))
            },
            render: () => <Spinner />,
        },
    )

    public publicProfileRoute = patternRoute<View, '/badges/user/:id'>('/badges/user/:id', {
        onEnter: (view, params) => {
            if (view.state.user?.id === Number(params.id)) {
                navigateTo('/badges')
                return
            }
            loadPlaytimeLeaderboardIfNeeded(view)
        },
        render: (view, params) => (
            <PublicProfileWrapper
                key={params.id}
                base={getBaseProps(view)}
                userId={Number(params.id)}
                leaderboard={loadPlaytimeLeaderboardIfNeeded(view)}
                view={view}
            />
        ),
    })

    // Preserve old bookmarks and links (Slack/email): /playtime/... → /badges/...
    // Old emails linked to /playtime/profile, which never had a /badges equivalent.
    public legacyRoute = prefixRoute<View>('/playtime', {
        onEnter: () => {
            const path = window.location.pathname
            navigateTo(
                path === '/playtime/profile' ? '/badges' : path.replace(/^\/playtime/, '/badges'),
            )
        },
        render: () => <Spinner />,
    })

    public menuItem: MenuItem = {
        text: t.playtime.profile,
        url: this.profileRoute.toUrl(),
        matchingRoutes: [
            this.profileRoute,
            this.surveyRoute,
            this.formPreviewRoute,
            this.campaignPreviewRoute,
            this.badgeRequestRoute,
            this.publicProfileRoute,
        ],
    }
}

export const playtimeModel = new PlaytimeModel()

const formatIsoDate = (date: Date): string => {
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

// Preview wrapper: loads whatever `load` resolves (a form wrapped as a
// campaign, or a real campaign) and renders the survey in preview mode.
const SurveyPreviewWrapper = (props: {
    base: ViewBaseProps
    view: View
    load: () => Promise<PlaytimeCampaignWithQuestions>
}) => {
    const [campaign, setCampaign] = useState<PlaytimeCampaignWithQuestions | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [failed, setFailed] = useState(false)
    const fetchStarted = useRef(false)

    if (!fetchStarted.current) {
        fetchStarted.current = true
        void (async () => {
            try {
                setCampaign(await props.load())
            } catch (error) {
                setFailed(true)
                handleError(props.view, error)
            } finally {
                setIsLoading(false)
            }
        })()
    }

    if (failed) {
        return (
            <PageShell className="playtime-page">
                <PageHeader title={t.playtime.q12.previewNotFound(props.view.state.lang)} />
            </PageShell>
        )
    }

    return (
        <SurveyView
            base={props.base}
            campaign={campaign}
            isLoading={isLoading}
            view={props.view}
            preview
        />
    )
}

// Survey wrapper component that handles async campaign loading
const SurveyViewWrapper = (props: {
    base: Parameters<typeof SurveyView>[0]['base']
    campaignId: number
    view: View
}) => {
    const [campaign, setCampaign] = useState<PlaytimeCampaignWithQuestions | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    // Load campaign on mount
    if (isLoading && !campaign) {
        void (async () => {
            try {
                const res = await props.view.api.playtime.getCampaign(props.campaignId)
                setCampaign(res.data)
            } catch (error) {
                handleError(props.view, error)
            } finally {
                setIsLoading(false)
            }
        })()
    }

    return (
        <SurveyView base={props.base} campaign={campaign} isLoading={isLoading} view={props.view} />
    )
}

// Public-profile wrapper: loads the viewed user's profile data and shows a
// not-found page for unknown/inactive users. Self-redirect happens in the
// route's onEnter, before this ever renders.
const PublicProfileWrapper = (props: {
    base: ViewBaseProps
    userId: number
    leaderboard: DataState<PlaytimeLeaderboardData>
    view: View
}) => {
    const [profile, setProfile] = useState<PlaytimeProfileData | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [notFound, setNotFound] = useState(!Number.isInteger(props.userId) || props.userId <= 0)
    const fetchStarted = useRef(false)

    const lang = props.view.state.lang

    if (!fetchStarted.current && !notFound) {
        fetchStarted.current = true
        void (async () => {
            try {
                const res = await props.view.api.playtime.getUserProfile(props.userId)
                setProfile(res.data)
            } catch (error) {
                if (error instanceof Error && error.message.includes('404')) {
                    setNotFound(true)
                } else {
                    handleError(props.view, error)
                }
            } finally {
                setIsLoading(false)
            }
        })()
    }

    if (notFound) {
        return (
            <PageShell className="playtime-page">
                <PageHeader title={t.playtime.publicProfile.notFound(lang)} />
            </PageShell>
        )
    }

    return (
        <PlaytimeProfileView
            base={props.base}
            profile={{ data: profile ?? undefined, isLoading, failCount: 0 }}
            leaderboard={props.leaderboard}
            totalBadgesCount={profile?.badges.length ?? 0}
            view={props.view}
            publicProfile={{ userId: props.userId, userName: profile?.userName ?? null }}
        />
    )
}
